import { queryClient } from 'index';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  createConfigurationQuestion,
  createQuestionOption,
  deleteConfigurationQuestion,
  deleteQuestionOption,
  getConfigurationQuestions,
  putConfigurationQuestions,
  reorderConfigurationQuestions,
  reorderQuestionOptions,
  updateConfigurationQuestion,
  updateQuestionOption,
} from 'services/ConfigurationQuestionService';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import { getToastErrorMessage, LogyxError } from 'services/api/errors';
import {
  ICreateOptionData,
  ICreateQuestionData,
  IReorderOptionsData,
  IReorderQuestionsData,
  IUpdateOptionData,
  IUpdateQuestionData,
} from 'types/ConfigurationQuestion.types';
import { ITestExtraPriceQuestion } from 'types/Configuration.types';

// The questions query returns questions with their options nested, so all
// option mutations invalidate the same query to refresh the manager.
export const useGetConfigurationQuestions = (
  configurationId: number | null | undefined
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_CONFIGURATION_QUESTIONS, configurationId],
    queryFn: () => getConfigurationQuestions(configurationId as number),
    enabled: !!configurationId,
  });

const invalidateQuestions = () =>
  queryClient.invalidateQueries([ReactQueryKeys.GET_CONFIGURATION_QUESTIONS]);

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

export const useCreateConfigurationQuestion = (configurationId: number) => {
  const { t } = useTranslation();
  return useMutation(
    (createQuestionData: ICreateQuestionData) =>
      createConfigurationQuestion(configurationId, createQuestionData),
    {
      onSuccess: () => {
        toast.success(t('Question added'));
        invalidateQuestions();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.CREATE_CONFIGURATION_QUESTION,
    }
  );
};

export const useUpdateConfigurationQuestion = (configurationId: number) => {
  const { t } = useTranslation();
  return useMutation(
    ({ questionId, data }: { questionId: number; data: IUpdateQuestionData }) =>
      updateConfigurationQuestion(configurationId, questionId, data),
    {
      onSuccess: () => {
        toast.success(t('Question updated'));
        invalidateQuestions();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.UPDATE_CONFIGURATION_QUESTION,
    }
  );
};

export const useReorderConfigurationQuestions = (configurationId: number) => {
  return useMutation(
    (data: IReorderQuestionsData) =>
      reorderConfigurationQuestions(configurationId, data),
    {
      onSuccess: () => invalidateQuestions(),
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
        invalidateQuestions();
      },
      mutationKey: ReactMutationKeys.REORDER_CONFIGURATION_QUESTIONS,
    }
  );
};

export const useDeleteConfigurationQuestion = (configurationId: number) => {
  const { t } = useTranslation();
  return useMutation(
    (questionId: number) =>
      deleteConfigurationQuestion(configurationId, questionId),
    {
      onSuccess: () => {
        toast.success(t('Question deleted'));
        invalidateQuestions();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.DELETE_CONFIGURATION_QUESTION,
    }
  );
};

// Bulk-replace a configuration's questions/options (test modal "Apply").
// configurationId is passed per-mutation because for a brand-new configuration
// the id only becomes known after it has been created.
export const usePutConfigurationQuestions = () => {
  const { t } = useTranslation();
  return useMutation(
    ({
      configurationId,
      data,
    }: {
      configurationId: number;
      data: { extra_price_questions: ITestExtraPriceQuestion[] };
    }) => putConfigurationQuestions(configurationId, data),
    {
      onSuccess: () => {
        toast.success(t('Extra price questions saved'));
        invalidateQuestions();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.PUT_CONFIGURATION_QUESTIONS,
    }
  );
};

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export const useCreateQuestionOption = (questionId: number) => {
  const { t } = useTranslation();
  return useMutation(
    (createOptionData: ICreateOptionData) =>
      createQuestionOption(questionId, createOptionData),
    {
      onSuccess: () => {
        toast.success(t('Option added'));
        invalidateQuestions();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.CREATE_QUESTION_OPTION,
    }
  );
};

export const useUpdateQuestionOption = (questionId: number) => {
  return useMutation(
    ({ optionId, data }: { optionId: number; data: IUpdateOptionData }) =>
      updateQuestionOption(questionId, optionId, data),
    {
      onSuccess: () => invalidateQuestions(),
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
        invalidateQuestions();
      },
      mutationKey: ReactMutationKeys.UPDATE_QUESTION_OPTION,
    }
  );
};

export const useReorderQuestionOptions = (questionId: number) => {
  return useMutation(
    (data: IReorderOptionsData) => reorderQuestionOptions(questionId, data),
    {
      onSuccess: () => invalidateQuestions(),
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
        invalidateQuestions();
      },
      mutationKey: ReactMutationKeys.REORDER_QUESTION_OPTIONS,
    }
  );
};

export const useDeleteQuestionOption = (questionId: number) => {
  const { t } = useTranslation();
  return useMutation(
    (optionId: number) => deleteQuestionOption(questionId, optionId),
    {
      onSuccess: () => {
        toast.success(t('Option deleted'));
        invalidateQuestions();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.DELETE_QUESTION_OPTION,
    }
  );
};
